import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import CountdownTimer from "./CountdownTimer";
import TippingMockup from "./TippingMockup";

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 20) setScrolled(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleScrollDown = () => {
    sectionRef.current?.nextElementSibling?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={sectionRef} className="relative flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center overflow-hidden px-6 pb-4 pt-20">
      {/* Radial glow */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--primary)/0.08)_0%,transparent_70%)]" />

      {/* Two-column wrapper */}
      <div className="relative z-10 flex w-full max-w-5xl flex-col items-center gap-12 md:flex-row md:items-center md:gap-16">
        {/* Left column — existing hero content */}
        <div className="flex-[3] text-center md:text-left">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
            THE NEW ERA OF F1 TIPPING.
          </p>

          <h1 className="mb-4 text-5xl font-black tracking-tighter sm:text-6xl lg:text-7xl">
            <span className="bg-gradient-to-r from-primary to-destructive bg-clip-text text-transparent italic">
              SEND IT
            </span>
          </h1>
          <p className="mb-6 text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl">
            Formula&nbsp;1 tipping for the modern fan.
          </p>

          <p className="mx-auto mb-8 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg md:mx-0">
            11 head-to-head match-ups every week. Who beats whom. No budget caps. Just pure racing instincts.
          </p>

          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:justify-center md:justify-start">
            <Button size="lg" className="text-base font-bold px-8 transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg hover:shadow-primary/25 animate-pulse [animation-duration:3s]" asChild>
              <Link to="/sign-up">Join the Grid (It's Free)</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-base font-bold px-8 transition-all duration-200 hover:scale-105 active:scale-95"
              asChild>
              <Link to="/sign-in">Sign In</Link>
            </Button>
          </div>

          <div className="mb-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground md:justify-start">
            <span className="flex items-center gap-1"><span className="text-emerald-500">✓</span> Free</span>
            <span className="flex items-center gap-1"><span className="text-emerald-500">✓</span> No credit card</span>
            <span className="flex items-center gap-1"><span className="text-emerald-500">✓</span> 30-second signup</span>
          </div>

          <CountdownTimer />
        </div>

        {/* Right column — tipping mockup */}
        <div className="flex-[2]">
          <TippingMockup />
        </div>
      </div>

      {/* Mobile scroll indicator */}
      <button
        onClick={handleScrollDown}
        aria-label="Scroll down"
        className={`absolute bottom-6 left-1/2 -translate-x-1/2 md:hidden transition-opacity duration-500 ${
          scrolled ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}>
        <ChevronDown className="w-6 h-6 text-foreground/40 animate-bounce [animation-duration:2s]" />
      </button>
    </section>
  );
}
